import React, { useEffect, useState } from 'react';
import { useAuth } from '../lib/AuthContext';
import { useOutlet } from '../lib/OutletContext';
import { getAudits, startAudit, submitAudit, getIngredients } from '../lib/api';
import { ClipboardCheck, Plus } from 'lucide-react';
import { formatNumber, formatDateTime } from '../lib/utils';
import { toast } from 'sonner';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import logger from '../lib/logger';

const Audits = () => {
  const { user } = useAuth();
  const { selectedOutlet } = useOutlet();
  const [audits, setAudits] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [currentAudit, setCurrentAudit] = useState(null);
  const [ingredients, setIngredients] = useState([]);
  const [auditLines, setAuditLines] = useState([]);
  // ISO date the physical count reflects. Defaults to today; operator can backdate.
  const [auditDate, setAuditDate] = useState(new Date().toISOString().slice(0, 10));

  useEffect(() => {
    loadAudits();
  }, []);

  const loadAudits = async () => {
    try {
      const outletId = selectedOutlet;
      const data = await getAudits(outletId);
      setAudits(data);
    } catch (error) {
      toast.error('Failed to load audits');
      logger.error(error);
    } finally {
      setLoading(false);
    }
  };

  const handleStartAudit = async () => {
    try {
      const outletId = selectedOutlet;
      const result = await startAudit(outletId, auditDate);
      const ingredientsRaw = await getIngredients(outletId);
      // getIngredients returns {ingredients, count, ...} — unwrap here.
      const ingredientsData = Array.isArray(ingredientsRaw)
        ? ingredientsRaw
        : (ingredientsRaw?.ingredients || []);

      setCurrentAudit(result);
      setIngredients(ingredientsData);
      setAuditLines(ingredientsData.map(ing => ({
        ingredient_id: ing.ingredient_id,
        ingredient_name: ing.ingredient_name,
        base_unit: ing.base_unit,
        expected: ing.soh,
        actual: ing.soh,
        avg_cost: ing.avg_cost,
        reason: 'UNKNOWN',
        notes: ''
      })));
      setDialogOpen(true);
    } catch (error) {
      toast.error('Failed to start audit');
      logger.error(error);
    }
  };

  const handleSubmitAudit = async () => {
    try {
      await submitAudit(currentAudit.audit_id, auditLines);
      toast.success('Audit submitted successfully');
      setDialogOpen(false);
      setCurrentAudit(null);
      setAuditLines([]);
      loadAudits();
    } catch (error) {
      toast.error('Failed to submit audit');
      logger.error(error);
    }
  };

  const updateAuditLine = (index, field, value) => {
    const updated = [...auditLines];
    updated[index][field] = value;
    setAuditLines(updated);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64" data-testid="loading-spinner">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-emerald-500"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="audits-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-black tracking-tight mb-2" data-testid="audits-title">Audits</h1>
          <p className="text-zinc-400">Physical stock verification and adjustments</p>
        </div>
        <div className="flex items-end gap-3">
          <div>
            <label htmlFor="audit-date" className="block text-xs text-zinc-400 mb-1">
              Audit as of
            </label>
            <input
              id="audit-date"
              type="date"
              value={auditDate}
              max={new Date().toISOString().slice(0, 10)}
              onChange={(e) => setAuditDate(e.target.value)}
              className="px-3 py-2 bg-[#09090b] border border-[#3f3f46] rounded-md focus:border-emerald-500 outline-none text-sm"
              data-testid="audit-date-input"
            />
          </div>
          <button
            onClick={handleStartAudit}
            className="flex items-center gap-2 bg-emerald-500 hover:bg-emerald-600 text-white font-bold py-2 px-4 rounded-md transition-colors"
            data-testid="start-audit-button"
          >
            <Plus className="w-4 h-4" />
            Start New Audit
          </button>
        </div>
      </div>

      {/* Audits List */}
      <div className="space-y-4" data-testid="audits-list">
        {audits.length === 0 ? (
          <div className="text-center py-12 bg-[#18181b] border border-[#27272a] rounded-lg">
            <ClipboardCheck className="w-12 h-12 text-zinc-600 mx-auto mb-3" />
            <p className="text-zinc-500">No audits found</p>
          </div>
        ) : (
          audits.map((audit) => (
            <div
              key={audit.audit_id}
              className="bg-[#18181b] border border-[#27272a] rounded-lg p-6"
              data-testid="audit-card"
            >
              <div className="flex items-start justify-between mb-4">
                <div>
                  <div className="flex items-center gap-3 mb-2">
                    <div className="w-10 h-10 rounded-lg bg-emerald-500/10 flex items-center justify-center">
                      <ClipboardCheck className="w-5 h-5 text-emerald-500" />
                    </div>
                    <div>
                      <h3 className="font-bold" data-testid="audit-id">Audit #{audit.audit_id.slice(0, 8)}</h3>
                      <p className="text-sm text-zinc-400">Started: {formatDateTime(audit.started_at)}</p>
                    </div>
                  </div>
                </div>
                <span className={`inline-flex items-center px-3 py-1 rounded-full text-xs font-medium border ${
                  audit.status === 'OPEN' ? 'bg-amber-500/10 text-amber-500 border-amber-500/20' :
                  audit.status === 'SUBMITTED' ? 'bg-emerald-500/10 text-emerald-500 border-emerald-500/20' :
                  'bg-zinc-500/10 text-zinc-500 border-zinc-500/20'
                }`}>
                  {audit.status}
                </span>
              </div>

              {audit.lines && audit.lines.length > 0 && (
                <div className="mt-4">
                  <p className="text-xs font-semibold text-zinc-400 uppercase tracking-wider mb-2">Variance Summary</p>
                  <div className="text-sm text-zinc-400">
                    {audit.lines.length} items audited
                  </div>
                </div>
              )}
            </div>
          ))
        )}
      </div>

      {/* Audit Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-4xl bg-[#18181b] border-[#27272a] text-white max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-xl font-bold">Physical Stock Audit</DialogTitle>
          </DialogHeader>
          <div className="mt-4 space-y-4">
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-[#09090b] sticky top-0">
                  <tr>
                    <th className="px-3 py-2 text-left text-xs text-zinc-400">Ingredient</th>
                    <th className="px-3 py-2 text-right text-xs text-zinc-400">Expected</th>
                    <th className="px-3 py-2 text-right text-xs text-zinc-400">Actual</th>
                    <th className="px-3 py-2 text-left text-xs text-zinc-400">Reason</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-[#27272a]">
                  {auditLines.map((line, idx) => (
                    <tr key={line.ingredient_id || `audit-${idx}`} className="hover:bg-[#09090b]">
                      <td className="px-3 py-2">{line.ingredient_name}</td>
                      <td className="px-3 py-2 text-right font-mono">
                        {formatNumber(line.expected)} {line.base_unit}
                      </td>
                      <td className="px-3 py-2">
                        <input
                          type="number"
                          step="0.01"
                          value={line.actual}
                          onChange={(e) => updateAuditLine(idx, 'actual', parseFloat(e.target.value))}
                          className="w-24 px-2 py-1 bg-[#09090b] border border-[#3f3f46] rounded text-right font-mono"
                          data-testid="actual-quantity-input"
                        />
                      </td>
                      <td className="px-3 py-2">
                        <select
                          value={line.reason}
                          onChange={(e) => updateAuditLine(idx, 'reason', e.target.value)}
                          className="px-2 py-1 bg-[#09090b] border border-[#3f3f46] rounded text-sm"
                          data-testid="reason-select"
                        >
                          <option value="UNKNOWN">Unknown</option>
                          <option value="WASTAGE">Wastage</option>
                          <option value="SPILLAGE">Spillage</option>
                          <option value="THEFT">Theft Suspected</option>
                        </select>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <button
              onClick={handleSubmitAudit}
              className="w-full bg-emerald-500 hover:bg-emerald-600 text-white font-bold py-3 px-4 rounded-md transition-colors"
              data-testid="submit-audit-button"
            >
              Submit Audit
            </button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Audits;
